// M1: AI 模型配置生成器 —— Settings 里的一个 section
//
// 用户在 UI 选模板 + 填表单 -> 实时生成 toml -> 一键 Copy / Download
// (Web 浏览器没法直写本机文件, 这是当前最优 UX; M2 Tauri sidecar 后能直接保存)

import { useEffect, useMemo, useState } from "react";

import {
  buildFullConfig,
  listAdapterTemplates,
  quotasToToml,
  type AdapterTemplate,
  type QuotaRow,
  type TemplateField,
} from "../lib/adapter-templates";
import { onRoomsChanged } from "../lib/room-bus";
import { getStorage, type StoredRoom } from "../lib/storage";
import {
  clearAgentHostBootLog,
  doctorAgentHost,
  getAgentHostStatus,
  isTauriDesktop,
  readAgentHostBootLog,
  restartAgentHost,
  saveAgentHostConfig,
  startAgentHost,
  stopAgentHost,
  type AgentHostDoctorResult,
  type AgentHostStatus,
} from "../lib/tauri-agent-host";
import { useI18n } from "../lib/i18n";
import { useDialog } from "./Dialog";

interface ConfiguredAdapter {
  templateId: string;
  values: Record<string, string>;
  toml: string;
  /** E9.9b: 访客配额, 序列化为 [[adapter.quotas]] toml 段附加到该 adapter 后 */
  quotas?: QuotaRow[];
}

const DRAFT_KEY = "chatbox.agentHost.configDraft.v1";

export function AdapterConfigSection() {
  const dlg = useDialog();
  const { t } = useI18n();
  const templates = listAdapterTemplates();
  const [activeTemplate, setActiveTemplate] = useState<AdapterTemplate | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [adapters, setAdapters] = useState<ConfiguredAdapter[]>([]);
  const [deviceId, setDeviceId] = useState("my-mac");
  const [desktopMode] = useState(() => isTauriDesktop());
  const [status, setStatus] = useState<AgentHostStatus | null>(null);
  const [doctorResult, setDoctorResult] = useState<AgentHostDoctorResult | null>(null);
  const [busy, setBusy] = useState(false);
  // M1.1: 用户选择 agent-host 加入哪些 Room
  const [availableRooms, setAvailableRooms] = useState<StoredRoom[]>([]);
  // null = 还没初始化; string[] = 用户已选的 room id 列表（空 [] = 不加入任何 Room）
  const [selectedRoomIds, setSelectedRoomIds] = useState<string[] | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw) as {
        deviceId?: string;
        adapters?: ConfiguredAdapter[];
        selectedRoomIds?: string[];
      };
      if (draft.deviceId) setDeviceId(draft.deviceId);
      if (Array.isArray(draft.adapters)) setAdapters(draft.adapters);
      if (Array.isArray(draft.selectedRoomIds)) setSelectedRoomIds(draft.selectedRoomIds);
    } catch {
      /* ignore bad draft */
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({ deviceId, adapters, selectedRoomIds }),
    );
  }, [adapters, deviceId, selectedRoomIds]);

  // M1.1: 拉真实 Room 列表 + 订阅变更
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      const s = await getStorage();
      const list = await s.listRooms();
      if (!alive) return;
      setAvailableRooms(list);
      // 首次启动或 draft 还没值时默认全选
      setSelectedRoomIds((prev) => {
        if (prev !== null) return prev;
        return list.map((r) => r.id);
      });
    };
    void refresh();
    const unsub = onRoomsChanged(() => {
      void refresh();
    });
    return () => {
      alive = false;
      unsub();
    };
  }, []);

  useEffect(() => {
    if (!desktopMode) return;
    void refreshStatus();
  }, [desktopMode]);

  // 实时生成单 adapter toml snippet (预览)
  const previewToml = useMemo(() => {
    if (!activeTemplate) return "";
    return activeTemplate.toToml(formValues);
  }, [activeTemplate, formValues]);

  // 合并所有已添加 adapter 成完整 config
  const fullConfig = useMemo(() => {
    if (adapters.length === 0) return "";
    return buildConfigFor(adapters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapters, deviceId, selectedRoomIds, availableRooms]);

  function buildConfigFor(list: ConfiguredAdapter[]) {
    // M1.1: 用户在 UI 勾的 Room 列表 → join_rooms。
    // selectedRoomIds === null 时（Storage 还没拉回）退回所有 availableRooms。
    const rooms =
      selectedRoomIds ?? availableRooms.map((r) => r.id);
    // E9.9b: 把 quotas 附加到对应 adapter toml 后面
    const snippets = list.map((a) => {
      const q = quotasToToml(a.quotas);
      return q ? `${a.toml}\n\n${q}` : a.toml;
    });
    return buildFullConfig(snippets, {
      device_id: deviceId,
      rooms: rooms.length > 0 ? rooms : ["default"],
    });
  }

  function pickTemplate(t: AdapterTemplate) {
    setActiveTemplate(t);
    // 初始化字段默认值
    const initial: Record<string, string> = {};
    for (const f of t.fields) {
      initial[f.key] = f.default ?? "";
    }
    setFormValues(initial);
  }

  async function applyDesktopConfig(list: ConfiguredAdapter[]) {
    if (!desktopMode || list.length === 0) return;
    setBusy(true);
    try {
      await saveAgentHostConfig(buildConfigFor(list));
      setStatus(await restartAgentHost());
      await refreshStatus();
    } catch (err) {
      await dlg.alert({ title: "应用配置失败", message: String(err) });
      await refreshStatus();
    } finally {
      setBusy(false);
    }
  }

  async function addToConfig() {
    if (!activeTemplate) return;
    // 必填校验
    for (const f of activeTemplate.fields) {
      if (f.required && !(formValues[f.key] ?? "").trim()) {
        void dlg.alert({ title: "缺必填项", message: `${f.label} 不能为空` });
        return;
      }
    }
    const next: ConfiguredAdapter = {
      templateId: activeTemplate.id,
      values: { ...formValues },
      toml: activeTemplate.toToml(formValues),
    };
    const id = (formValues.id ?? "").trim();
    if (id && adapters.some((a) => (a.values.id ?? "").trim() === id)) {
      void dlg.alert({
        title: "Adapter ID 重复",
        message: `已经有一个 id = ${id} 的 AI，请换一个名字`,
      });
      return;
    }
    const nextAdapters = [...adapters, next];
    setAdapters(nextAdapters);
    setActiveTemplate(null);
    setFormValues({});
    await applyDesktopConfig(nextAdapters);
  }

  // E9.9b: 更新某 adapter 的 quotas 数组, 触发 toml 重 build + 应用
  function updateQuotas(index: number, next: QuotaRow[]) {
    setAdapters((prev) => {
      const copy = prev.slice();
      copy[index] = { ...copy[index], quotas: next };
      // 防抖式 apply: 同步 localStorage, 但不每次按键都重启 agent-host
      // (用户编辑完 user_id 字段后会按"保存并重启"按钮明确触发)
      return copy;
    });
  }

  async function removeAdapter(index: number) {
    const nextAdapters = adapters.filter((_, j) => j !== index);
    setAdapters(nextAdapters);
    if (nextAdapters.length > 0) {
      await applyDesktopConfig(nextAdapters);
    }
  }

  async function copyToClipboard(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      await dlg.alert({ title: "已复制", message: `${label} 已复制到剪贴板` });
    } catch {
      await dlg.alert({ title: "复制失败", message: "请手动选中下方文本框" });
    }
  }

  function downloadToml(text: string, filename: string) {
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function refreshStatus() {
    try {
      setStatus(await getAgentHostStatus());
    } catch (err) {
      setStatus({
        available: false,
        running: false,
        config_path: "~/.chatbox/agent-host.toml",
        exe_path: null,
        error: String(err),
      });
    }
  }

  async function runDesktopAction(action: "save" | "doctor" | "start" | "stop" | "restart") {
    if (!fullConfig && (action === "save" || action === "restart" || action === "doctor")) {
      await dlg.alert({ title: "还没有配置", message: "请先添加至少一个 AI 模型" });
      return;
    }
    setBusy(true);
    try {
      if (action === "save") {
        const path = await saveAgentHostConfig(fullConfig);
        await dlg.alert({ title: "已保存", message: `配置已写入 ${path}` });
      } else if (action === "doctor") {
        await saveAgentHostConfig(fullConfig);
        const result = await doctorAgentHost();
        setDoctorResult(result);
        if (!result.ok) {
          await dlg.alert({ title: "配置校验失败", message: result.stderr || result.stdout || "agent-host doctor 返回失败" });
        }
      } else if (action === "start") {
        setStatus(await startAgentHost());
      } else if (action === "stop") {
        setStatus(await stopAgentHost());
      } else {
        await saveAgentHostConfig(fullConfig);
        setStatus(await restartAgentHost());
      }
      await refreshStatus();
    } catch (err) {
      await dlg.alert({ title: "操作失败", message: String(err) });
      await refreshStatus();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="adapter-config">
      <p className="muted small">
        {t("adapter.intro.head")} <code>agent-host.toml</code>。
        {desktopMode ? t("adapter.intro.desktop") : t("adapter.intro.web")}
      </p>

      {/* 已添加列表 */}
      {adapters.length > 0 && (
        <div className="adapter-config__added-wrap">
          <div className="adapter-config__added-head">
            <strong>{t("adapter.configured.head", { count: adapters.length })}</strong>
            <span className="muted small">
              {desktopMode ? t("adapter.sub.desktop") : t("adapter.sub.web")}
            </span>
          </div>
          <ul className="adapter-config__added">
            {adapters.map((a, i) => (
              <li key={i} className="adapter-config__added-item adapter-config__added-item--col">
                <div className="adapter-config__added-row">
                  <div>
                    <strong>{templates.find((t) => t.id === a.templateId)?.label}</strong>
                    <span className="muted small">
                      {" · "}@{a.values.id ?? "(default)"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="link-btn link-btn--danger"
                    disabled={busy}
                    onClick={() => void removeAdapter(i)}
                  >
                    {t("adapter.list.remove")}
                  </button>
                </div>
                {/* E9.9b: per-adapter 访客配额编辑 */}
                <QuotaEditor
                  quotas={a.quotas ?? []}
                  onChange={(next) => updateQuotas(i, next)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 模板选择 */}
      {!activeTemplate && (
        <>
          <div className="adapter-config__templates">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                className="adapter-config__template"
                onClick={() => pickTemplate(t)}
              >
                <strong>{t.label}</strong>
                <span className="muted small">{t.description}</span>
              </button>
            ))}
          </div>
          <p className="muted small" style={{ marginTop: 8 }}>
            {t("adapter.template.add.tip")}
          </p>
        </>
      )}

      {/* 表单 */}
      {activeTemplate && (
        <div className="adapter-config__form">
          <div className="adapter-config__form-head">
            <strong>{activeTemplate.label}</strong>
            <button
              type="button"
              className="link-btn"
              onClick={() => setActiveTemplate(null)}
            >
              {t("adapter.form.cancel")}
            </button>
          </div>
          {activeTemplate.preInstall && activeTemplate.preInstall.length > 0 && (
            <div className="adapter-config__pre">
              <div className="muted small" style={{ marginBottom: 4 }}>
                {t("adapter.form.preInstall")}
              </div>
              <pre>{activeTemplate.preInstall.join("\n")}</pre>
            </div>
          )}
          {activeTemplate.fields.map((f) => (
            <FieldInput
              key={f.key}
              field={f}
              value={formValues[f.key] ?? ""}
              onChange={(v) => setFormValues((p) => ({ ...p, [f.key]: v }))}
            />
          ))}
          <details className="adapter-config__preview">
            <summary>{t("adapter.form.preview")}</summary>
            <pre>{previewToml}</pre>
          </details>
          <div className="settings__edit-row" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={() => void addToConfig()}
            >
              {desktopMode ? t("adapter.form.add.apply") : t("adapter.form.add")}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setActiveTemplate(null)}
            >
              {t("adapter.form.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* 完整 config 输出 */}
      {adapters.length > 0 && (
        <section className="adapter-config__output">
          {/* M1.1: 加入哪些会话 —— 用户在 UI 勾选, agent-host 重启后生效 */}
          <div className="adapter-config__rooms">
            <strong>{t("adapter.rooms.title")}</strong>
            <p className="muted small" style={{ marginTop: 2 }}>
              {t("adapter.rooms.sub.full")}
            </p>
            {availableRooms.length === 0 ? (
              <p className="muted small">{t("adapter.rooms.empty.cta")}</p>
            ) : (
              <div className="adapter-config__rooms-list">
                {availableRooms.map((r) => {
                  const checked = (selectedRoomIds ?? []).includes(r.id);
                  return (
                    <label key={r.id} className="adapter-config__room-chk">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setSelectedRoomIds((prev) => {
                            const cur = prev ?? availableRooms.map((x) => x.id);
                            if (e.target.checked) {
                              return cur.includes(r.id) ? cur : [...cur, r.id];
                            }
                            return cur.filter((id) => id !== r.id);
                          });
                        }}
                      />
                      <span>{r.name}</span>
                      <span className="muted small">@{r.id}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <details className="adapter-config__advanced">
            <summary>{t("adapter.advanced.summary")}</summary>
            <div className="settings__edit-row" style={{ marginTop: 8 }}>
              <label className="muted small" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                device_id:
                <input
                  className="input"
                  value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)}
                  style={{ width: 180 }}
                />
              </label>
            </div>
            <pre className="adapter-config__toml">{fullConfig}</pre>
          </details>
          {desktopMode ? (
            <DesktopAgentControls
              busy={busy}
              status={status}
              doctorResult={doctorResult}
              onSave={() => void runDesktopAction("save")}
              onDoctor={() => void runDesktopAction("doctor")}
              onStart={() => void runDesktopAction("start")}
              onStop={() => void runDesktopAction("stop")}
              onRestart={() => void runDesktopAction("restart")}
              onRefresh={() => void refreshStatus()}
            />
          ) : (
            <>
              <div className="settings__edit-row">
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => void copyToClipboard(fullConfig, "完整 toml")}
                >
                  {t("adapter.web.copy")}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => downloadToml(fullConfig, "agent-host.toml")}
                >
                  {t("adapter.web.download")}
                </button>
              </div>
              <p className="muted small" style={{ marginTop: 8 }}>
                {t("adapter.web.runHint.line1")} <code>~/.chatbox/agent-host.toml</code>, {t("adapter.web.runHint.line2")}
                <br />
                <code>cargo run -p chatbox-agent-host -- start --config ~/.chatbox/agent-host.toml</code>
              </p>
            </>
          )}
        </section>
      )}
    </div>
  );
}

// E9.9b: 单个 adapter 的访客配额编辑 — 折叠 + 增删行 + 字段实时同步
function QuotaEditor({
  quotas,
  onChange,
}: {
  quotas: QuotaRow[];
  onChange: (next: QuotaRow[]) => void;
}) {
  const { t } = useI18n();
  function updateRow(idx: number, patch: Partial<QuotaRow>) {
    const next = quotas.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  }
  function addRow() {
    onChange([...quotas, { user_id: "" }]);
  }
  function removeRow(idx: number) {
    onChange(quotas.filter((_, i) => i !== idx));
  }
  return (
    <details className="quota-editor">
      <summary>
        {t("adapter.quota.summary", { n: quotas.length })}
        <span className="muted small" style={{ marginLeft: 6 }}>
          {t("adapter.quota.summary.sub")}
        </span>
      </summary>
      <div className="quota-editor__body">
        {quotas.length === 0 && (
          <p className="muted small">
            {t("adapter.quota.empty")}
          </p>
        )}
        {quotas.map((q, idx) => (
          <div key={idx} className="quota-editor__row">
            <input
              className="input"
              placeholder={t("adapter.quota.col.user")}
              value={q.user_id}
              onChange={(e) => updateRow(idx, { user_id: e.target.value })}
              style={{ flex: 1, minWidth: 100 }}
            />
            <input
              className="input"
              type="number"
              placeholder={t("adapter.quota.col.dailyTok")}
              value={q.daily_token_limit ?? ""}
              onChange={(e) =>
                updateRow(idx, {
                  daily_token_limit: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              style={{ width: 100 }}
              min="0"
            />
            <input
              className="input"
              type="number"
              step="0.01"
              placeholder={t("adapter.quota.col.dailyUsd")}
              value={q.daily_usd_limit ?? ""}
              onChange={(e) =>
                updateRow(idx, {
                  daily_usd_limit: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              style={{ width: 80 }}
              min="0"
            />
            <input
              className="input"
              type="number"
              placeholder={t("adapter.quota.col.monthlyTok")}
              value={q.monthly_token_limit ?? ""}
              onChange={(e) =>
                updateRow(idx, {
                  monthly_token_limit: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              style={{ width: 100 }}
              min="0"
            />
            <input
              className="input"
              type="number"
              step="0.01"
              placeholder={t("adapter.quota.col.totalUsd")}
              value={q.total_usd_limit ?? ""}
              onChange={(e) =>
                updateRow(idx, {
                  total_usd_limit: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              style={{ width: 80 }}
              min="0"
            />
            <button
              type="button"
              className="link-btn link-btn--danger"
              onClick={() => removeRow(idx)}
              title={t("adapter.quota.row.remove")}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className="link-btn"
          onClick={addRow}
          style={{ marginTop: 6 }}
        >
          {t("adapter.quota.row.add")}
        </button>
        <p className="muted small" style={{ marginTop: 6 }}>
          {t("adapter.quota.foot")}
        </p>
      </div>
    </details>
  );
}

function DesktopAgentControls({
  busy,
  status,
  doctorResult,
  onSave,
  onDoctor,
  onStart,
  onStop,
  onRestart,
  onRefresh,
}: {
  busy: boolean;
  status: AgentHostStatus | null;
  doctorResult: AgentHostDoctorResult | null;
  onSave: () => void;
  onDoctor: () => void;
  onStart: () => void;
  onStop: () => void;
  onRestart: () => void;
  onRefresh: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="adapter-config__desktop">
      <div className="adapter-config__status">
        <span
          className={`adapter-config__dot ${
            status?.running ? "adapter-config__dot--ok" : "adapter-config__dot--idle"
          }`}
        />
        <strong>{status?.running ? t("agentHost.running") : t("agentHost.idle")}</strong>
        {status?.available === false && (
          <span className="muted small"> · {status.error ?? t("agentHost.notFound")}</span>
        )}
      </div>
      {status && (
        <div className="muted small adapter-config__paths">
          <div>{t("agentHost.paths.config")} {status.config_path}</div>
          {status.exe_path && <div>{t("agentHost.paths.exe")} {status.exe_path}</div>}
        </div>
      )}
      <div className="settings__edit-row">
        <button type="button" className="btn btn--primary" disabled={busy} onClick={onRestart}>
          {t("agentHost.btn.restart")}
        </button>
        <button type="button" className="btn btn--ghost" disabled={busy} onClick={onDoctor}>
          {t("agentHost.btn.doctor")}
        </button>
        <button type="button" className="btn btn--ghost" disabled={busy} onClick={onSave}>
          {t("agentHost.btn.save")}
        </button>
        <button type="button" className="btn btn--ghost" disabled={busy || status?.running} onClick={onStart}>
          {t("agentHost.btn.start")}
        </button>
        <button type="button" className="btn btn--ghost" disabled={busy || !status?.running} onClick={onStop}>
          {t("agentHost.btn.stop")}
        </button>
        <button type="button" className="btn btn--ghost" disabled={busy} onClick={onRefresh}>
          {t("agentHost.btn.refresh")}
        </button>
      </div>
      {doctorResult && (
        <details className="adapter-config__preview" open={!doctorResult.ok}>
          <summary>{doctorResult.ok ? t("agentHost.doctor.ok") : t("agentHost.doctor.bad")}</summary>
          <pre>{[doctorResult.stdout, doctorResult.stderr].filter(Boolean).join("\n")}</pre>
        </details>
      )}
      <BootLogPanel />
    </div>
  );
}

function BootLogPanel() {
  const dlg = useDialog();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const txt = await readAgentHostBootLog();
      setContent(txt);
    } catch (e) {
      setContent(`读取失败: ${e}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleClear() {
    const ok = await dlg.confirm({
      title: "清空启动日志",
      message: "确认清空 ~/.chatbox/agent-host-boot.log？下次启动会重新记录。",
      confirmLabel: "清空",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await clearAgentHostBootLog();
      setContent(t("bootlog.content.cleared"));
    } catch (e) {
      await dlg.alert({ title: "清空失败", message: String(e) });
    }
  }

  async function handleCopy() {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      await dlg.alert({ title: "已复制", message: "日志已复制到剪贴板" });
    } catch {
      /* ignore */
    }
  }

  return (
    <details
      className="adapter-config__preview"
      onToggle={(e) => {
        const isOpen = (e.currentTarget as HTMLDetailsElement).open;
        setOpen(isOpen);
        if (isOpen && !content) void refresh();
      }}
    >
      <summary>{t("adapter.bootlog.summary")}</summary>
      {open && (
        <>
          <div className="settings__edit-row" style={{ marginBottom: 8 }}>
            <button
              type="button"
              className="link-btn"
              onClick={() => void refresh()}
              disabled={loading}
            >
              {loading ? t("bootlog.btn.refreshing") : t("bootlog.btn.refresh")}
            </button>
            <button
              type="button"
              className="link-btn"
              onClick={() => void handleCopy()}
              disabled={!content}
            >
              {t("bootlog.btn.copy")}
            </button>
            <button
              type="button"
              className="link-btn link-btn--danger"
              onClick={() => void handleClear()}
            >
              {t("bootlog.btn.clear")}
            </button>
          </div>
          <pre style={{ maxHeight: 280, overflow: "auto" }}>{content || t("bootlog.content.empty")}</pre>
        </>
      )}
    </details>
  );
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: TemplateField;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="adapter-config__field">
      <label className="adapter-config__field-label">
        {field.label}
        {field.required && <span style={{ color: "var(--bad)" }}> *</span>}
      </label>
      {field.type === "select" && field.options ? (
        <select
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {field.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <input
          className="input"
          type={field.type === "password" ? "password" : "text"}
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={field.type === "password" ? "off" : undefined}
          spellCheck={false}
        />
      )}
      {field.help && <div className="muted small">{field.help}</div>}
    </div>
  );
}
